// =============================================================================
// components/KpiRules/EngineLog.jsx
// Mostra historico de execucoes do motor + trigger manual.
// Suporta 3 acoes:
//   - Atualizar Baselines (apenas)
//   - Detetar Anomalias (apenas)
//   - Pipeline Completo (baseline + detecao)
// =============================================================================
import { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Chip, Stack, Button, TextField,
  CircularProgress, Alert, Divider, ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import PlayArrowIcon  from '@mui/icons-material/PlayArrow';
import RefreshIcon    from '@mui/icons-material/Refresh';
import TimelineIcon   from '@mui/icons-material/Timeline';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

import { useKpiRules } from '../../hooks/useKpi';
import {
  apiRunBaseline,
  apiRunDetection,
  apiRunFullPipeline,
} from '../../requests/kpiRequests';

const STATUS_COLOR = {
  Running: 'info',
  Success: 'success',
  Error:   'error',
};

const MODES = {
  baseline: {
    label: 'Baseline',
    icon: <TimelineIcon fontSize="small" />,
    req: apiRunBaseline,
    defaultDate: () => yesterdayIso(),
  },
  detection: {
    label: 'Detecao',
    icon: <TroubleshootIcon fontSize="small" />,
    req: apiRunDetection,
    defaultDate: () => yesterdayIso(),
  },
  full: {
    label: 'Completo',
    icon: <AllInclusiveIcon fontSize="small" />,
    req: apiRunFullPipeline,
    defaultDate: () => yesterdayIso(),
  },
};

function todayIso() {
  return new Date().toISOString().split('T')[0];
}
function yesterdayIso() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

export default function EngineLog() {
  const { engineLogs, engineLogsLoading, loadLogs } = useKpiRules();

  const [mode, setMode]   = useState('detection');
  const [date, setDate]   = useState(yesterdayIso());
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);
  const [runOk, setRunOk]   = useState(null);

  useEffect(() => { loadLogs(); }, []); // eslint-disable-line

  const handleModeChange = (_, newMode) => {
    if (!newMode) return;
    setMode(newMode);
    setDate(MODES[newMode].defaultDate());
    setRunOk(null);
    setRunError(null);
  };

  const handleRun = async () => {
    setRunning(true);
    setRunError(null);
    setRunOk(null);
    try {
      const res = await MODES[mode].req(date);
      setRunOk(res.data?.message || 'Executado.');
      loadLogs();
    } catch (err) {
      setRunError(err.response?.data?.error || err.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          Execucoes do Motor
        </Typography>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
          onClick={loadLogs} disabled={engineLogsLoading}>
          Atualizar
        </Button>
      </Box>

      {/* Trigger manual */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          EXECUCAO MANUAL
        </Typography>

        <Box sx={{ mt: 1, mb: 1 }}>
          <ToggleButtonGroup
            value={mode} exclusive onChange={handleModeChange}
            size="small" color="primary" fullWidth
          >
            <Tooltip title="So atualizar baselines (sem detetar)">
              <ToggleButton value="baseline">
                {MODES.baseline.icon}&nbsp;Baseline
              </ToggleButton>
            </Tooltip>
            <Tooltip title="So detetar (assume baseline ja existe)">
              <ToggleButton value="detection">
                {MODES.detection.icon}&nbsp;Detecao
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Baseline + Detecao para a mesma data">
              <ToggleButton value="full">
                {MODES.full.icon}&nbsp;Pipeline
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            type="date" size="small" label="Data de referencia"
            value={date} onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: 200 }}
          />
          <Button
            variant="contained" size="small"
            startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
            onClick={handleRun} disabled={running}
          >
            {running ? 'A correr...' : `Correr ${MODES[mode].label}`}
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {mode === 'baseline'  && 'Atualiza baselines absorvendo os contadores desta data + 3 semanas anteriores.'}
          {mode === 'detection' && 'Deteta anomalias nos contadores desta data (baseline tem que existir).'}
          {mode === 'full'      && 'Deteta anomalias nesta data e em seguida absorve-os na baseline.'}
        </Typography>

        {runOk    && <Alert severity="success" sx={{ mt: 1, py: 0 }}>{runOk}</Alert>}
        {runError && <Alert severity="error"   sx={{ mt: 1, py: 0 }}>{runError}</Alert>}
      </Paper>

      <Divider sx={{ mb: 1.5 }} />

      {engineLogsLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!engineLogsLoading && engineLogs.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          Sem execucoes registadas.
        </Typography>
      )}

      <Stack spacing={1}>
        {engineLogs.map((log) => (
          <Paper key={log.logId} variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                {new Date(log.runDate).toLocaleDateString('pt-PT')}
              </Typography>
              <Chip label={log.status} size="small"
                color={STATUS_COLOR[log.status] || 'default'} />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Inicio: {new Date(log.startTime).toLocaleString('pt-PT')}
              {log.endTime && ` - Fim: ${new Date(log.endTime).toLocaleString('pt-PT')}`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
              <Typography variant="caption">
                <strong>+{log.incidentsOpened}</strong> abertos
              </Typography>
              <Typography variant="caption">
                <strong>~{log.incidentsUpdated}</strong> mantidos
              </Typography>
              <Typography variant="caption">
                <strong>~{log.incidentsClosed}</strong> fechados
              </Typography>
            </Box>
            {log.errorMessage && (
              <Alert severity="warning" sx={{ mt: 1, py: 0, fontSize: '0.75rem' }}>
                {log.errorMessage}
              </Alert>
            )}
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
}
